import React, { useContext, useEffect, useState } from "react";
import { Col, Form, InputGroup, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  cleanInput,
  normalizeInputValue,
  validate,
} from "../utils/numberInputUtils";
import HelpTextAccordion from "./HelpTextAccordion";
import NumberInputLabel from "./NumberInputLabel";
import ValidationContext from "./ValidationContext";

interface Props {
  fieldName: string;
  initialValue: undefined | null | string | number;
  onChange?: (value: undefined | string, valid: boolean) => void;
  onValidValueChange?: (value?: number) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  required?: boolean;
  onlyInts?: boolean;
  autoFocus?: boolean;
  size?: "sm" | "lg";
  style?: any;
  extraInformationField?: string;
}

const NumberInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      fieldName,
      initialValue,
      onChange,
      onValidValueChange,
      min = Number.MIN_VALUE,
      max = Number.MAX_VALUE,
      onlyInts = false,
      required = false,
      size = undefined,
      placeholder = undefined,
      style = undefined,
      autoFocus,
      extraInformationField,
    },
    forwardedRef,
  ) => {
    const { t } = useTranslation();
    const { validated, validationCounter, suppressFieldErrors } = useContext(
      ValidationContext,
    );
    const unit =
      t(`${fieldName}.unit`) === `${fieldName}.unit`
        ? ""
        : t(`${fieldName}.unit`);

    const [displayValue, setDisplayValue] = useState<undefined | string>(
      undefined,
    );
    const [isValid, setValid] = useState<boolean>(true);
    const [hasBeenChanged, setHasBeenChanged] = useState(false);

    useEffect(() => {
      if (displayValue === undefined && initialValue) {
        setDisplayValue(`${initialValue}`);
        setValid(
          validate(`${initialValue}`, {
            validationCounter,
            required,
            onlyInts,
            min,
            max,
          }),
        );
      }
    }, [
      initialValue,
      displayValue,
      validationCounter,
      required,
      onlyInts,
      min,
      max,
    ]);

    useEffect(() => {
      validateAndUpdate(displayValue || "");
    }, [min, max, required, onlyInts, validationCounter]);

    const validateAndUpdate = (value: string) => {
      const isValid = validate(value, {
        validationCounter,
        required,
        onlyInts,
        min,
        max,
      });

      if (isValid) {
        setDisplayValue(normalizeInputValue(value));
        setValid(true);
        if (onChange) {
          onChange(cleanInput(value), true);
        }
        if (onValidValueChange) {
          onValidValueChange(Number.parseInt(cleanInput(value)));
        }
      } else {
        setDisplayValue(value);
        setValid(false);
        if (onChange) {
          onChange(undefined, false);
        }
        if (onValidValueChange) {
          onValidValueChange(undefined);
        }
      }
    };

    const effectivePlaceholder =
      placeholder || (required ? t("required") : undefined);

    const labelId = `${fieldName}-label`;
    const unitId = `${fieldName}-unit`;
    const helpTextId = `${fieldName}-help`;
    const errorId = `${fieldName}-error`;
    const descriptionIds = [unitId, helpTextId];
    if (extraInformationField) {
      descriptionIds.push(`${fieldName}-info`);
    }
    const isInvalid = (hasBeenChanged || validated) && !isValid;

    return (
      <Form.Group
        className={`form-group ${hasBeenChanged && !isValid ? "is-invalid invalid" : ""}`}
      >
        <Row>
          <Col md={12}>
            <NumberInputLabel
              labelId={labelId}
              fieldName={fieldName}
              required={required}
            />
            <HelpTextAccordion fieldName={fieldName} id={helpTextId} />
          </Col>
          <Col md={size === "sm" ? 6 : 12}>
            <InputGroup>
              <Form.Control
                id={fieldName}
                data-testid={fieldName}
                ref={forwardedRef}
                size={size}
                type="text"
                inputMode="numeric"
                style={style}
                autoFocus={autoFocus}
                className={`value-input ${
                  hasBeenChanged || validated ? "changed" : ""
                } ${hasBeenChanged && !isValid ? "is-invalid invalid" : ""}`}
                placeholder={effectivePlaceholder}
                value={displayValue || ""}
                onBlur={() => {
                  setHasBeenChanged(true);
                }}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setHasBeenChanged(false);
                  validateAndUpdate(value);
                }}
                required={required}
                isValid={(hasBeenChanged || validated) && isValid}
                isInvalid={isInvalid}
                aria-labelledby={labelId}
                aria-describedby={[
                  ...descriptionIds,
                  isInvalid && !suppressFieldErrors ? errorId : undefined,
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-invalid={isInvalid || undefined}
              ></Form.Control>
              {unit && <InputGroup.Text id={unitId}>{unit}</InputGroup.Text>}
              <Form.Control.Feedback
                id={errorId}
                type="invalid"
                aria-hidden={suppressFieldErrors || undefined}
              >
                {t(`${fieldName}.invalid`)}
              </Form.Control.Feedback>
            </InputGroup>
            {extraInformationField?.trim && (
              <div id={`${fieldName}-info`} className="extra-information-field">
                {extraInformationField}
              </div>
            )}
          </Col>
        </Row>
      </Form.Group>
    );
  },
);

export default NumberInput;
